/**
 * 고친 이유 창 — 카탈로그 확정 행 드로어의 [저장] 이 연다(현민 2026-10-27 B 안 — 드로어 맨 위 칸 대신 팝업).
 * 무엇을 바꿨는지(칸 old → new)를 보이고 이유 한 줄을 받는다. 저장은 하지 않는다 — 받은 이유로 부르는 쪽(드로어)이 저장하고 실패 글도 드로어에 남는다.
 * 창은 document.body 에 붙였다가 닫으면 뗀다(합치기 확인창 꼴 · mg-* 틀을 같이 쓴다).
 */
import { escapeHtml } from '../lib/cells';

/** 바뀐 칸 한 줄 — 라벨 · 지금 값 · 새 값(화면 글) */
export type ReasonChange = { label: string; old: string; new: string };

const REASON_REQUIRED_TEXT = '고친 이유를 한 줄 적어 주세요 — 슬랙 알림과 변경 이력에 남습니다';

/** 창을 열고 이유를 기다린다. [저장] · Enter 면 다듬은 이유 글, [취소] · 바깥 · Esc 면 null */
export async function openReasonDialog(changes: readonly ReasonChange[]): Promise<string | null> {
  const host = document.createElement('div');
  host.innerHTML = renderReasonDialog(changes);
  document.body.appendChild(host);
  const input = host.querySelector<HTMLInputElement>('[data-action="reason-input"]');
  const errorLine = host.querySelector<HTMLElement>('[data-role="reason-error"]');
  input?.focus();

  return new Promise((resolve) => {
    function close(result: string | null): void {
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    function confirm(): void {
      const reason = (input?.value ?? '').trim();
      if (reason === '') {
        if (errorLine !== null) errorLine.textContent = REASON_REQUIRED_TEXT;
        input?.focus();
        return;
      }
      close(reason);
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close(null);
      if (event.key === 'Enter' && event.target === input) { event.preventDefault(); confirm(); }
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'reason-cancel') close(null);
      if (target.dataset.action === 'reason-confirm') confirm();
    }

    host.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
  });
}

/** 창 전체 — 바뀐 칸 목록 · 이유 한 줄 · 실패 글 자리 · [취소] [저장] */
function renderReasonDialog(changes: readonly ReasonChange[]): string {
  const rows = changes.map((change) => `
          <div class="rd-change"><b>${escapeHtml(change.label)}</b> <span class="crd-old">${escapeHtml(change.old || '—')}</span> → <span class="crd-new">${escapeHtml(change.new || '—')}</span></div>`).join('');
  return `
    <div class="mg-wrap" role="dialog" aria-label="고친 이유">
      <div class="mg-overlay" data-action="reason-cancel"></div>
      <div class="mg-dialog">
        <h3 class="mg-title">고친 이유</h3>
        <div class="rd-changes">${rows}</div>
        <input type="text" class="input" data-action="reason-input" placeholder="예: 퀵스펙 16301 기준으로 용량 정정">
        <p class="mg-note">슬랙 #53-플랫폼-카탈로그관리 알림과 변경 이력에 남습니다.</p>
        <p class="text-danger rd-error" data-role="reason-error"></p>
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="reason-cancel">취소</button>
          <button type="button" class="btn-primary" data-action="reason-confirm">저장</button>
        </div>
      </div>
    </div>`;
}
